import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowRight, Headphones, BookOpen, Play, Map } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLessonPurchases } from "@/hooks/useUserPurchases";
import { useFeaturedLesson } from "@/hooks/useFeaturedLesson";
import { useActiveLesson } from "@/hooks/useLessons";
import { resolveLessonPrice, useLessonsPromoUntil } from "@/lib/lessonsPromo";
import { useBooks } from "@/hooks/useBooks";
import { useBookImages } from "@/hooks/useBookImages";
import { Button } from "@/components/ui/button";
import LessonArtwork, { pickArt } from "@/components/LessonArtwork";
import { getThumbUrl } from "@/lib/image-utils";
import { toVocative } from "@/lib/lithuanian-vocative";
import { supabase } from "@/integrations/supabase/client";
import dusiaLogo from "@/assets/dusia-final.png";

/** Truncate at last word boundary before `max`, append ellipsis if shortened. */
const smartTruncate = (text: string, max = 160): string => {
  if (!text) return "";
  if (text.length <= max) return text;
  const slice = text.slice(0, max);
  const lastSpace = slice.lastIndexOf(" ");
  const cut = lastSpace > max * 0.6 ? slice.slice(0, lastSpace) : slice;
  return cut.replace(/[\s,.;:–—-]+$/, "") + "…";
};

type AdKind = "lecture" | "book" | "welcome" | "announcement" | "promo";

interface AdLink {
  text: string;
  href: string;
  external?: boolean;
}

interface AdSlide {
  id: string;
  kind: AdKind;
  eyebrow: string;
  headline: string;
  sub: string;
  primary: AdLink;
  secondary?: AdLink;
  price?: string;
  /** Render-side mockup */
  mockup: React.ReactNode;
}

/* ──────────────────────────────────────────────
   Mockup: LECTURE — phone-shaped audio card
   ────────────────────────────────────────────── */
const LectureMockup = ({
  title,
  duration,
  price,
  artSeed,
}: {
  title: string;
  duration: string;
  price: string;
  artSeed: string;
}) => {
  const art = pickArt(artSeed);
  return (
    <div className="relative h-full aspect-[9/16] mx-auto">
      {/* Bauhaus accent behind */}
      <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-[hsl(var(--yellow))] opacity-90" />
      <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-[hsl(var(--ink))] rotate-12" />

      {/* Phone frame */}
      <div className="relative h-full rounded-[2.2rem] bg-[hsl(var(--ink))] p-2 shadow-[0_30px_60px_-25px_rgba(30,30,30,0.55)]">
        <div className="relative h-full w-full rounded-[1.8rem] overflow-hidden bg-[hsl(var(--cream-soft))]">
          {/* Notch */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 h-1.5 w-16 rounded-full bg-[hsl(var(--ink))] z-10" />

          {/* Top half: artwork */}
          <div className="relative h-[55%] w-full">
            <LessonArtwork
              variant={art.variant}
              paletteIndex={art.paletteIndex}
              title={title}
              className="absolute inset-0"
            />
            {/* AUDIO badge */}
            <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[hsl(var(--ink))] text-[hsl(var(--cream))] text-[10px] font-bold tracking-[0.18em] uppercase">
              <Headphones size={10} /> Audio
            </div>
          </div>

          {/* Bottom: meta + player */}
          <div className="absolute bottom-0 left-0 right-0 h-[45%] p-4 flex flex-col justify-between bg-[hsl(var(--cream-soft))]">
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[hsl(var(--ink))]/55 font-semibold mb-1.5">
                Pamoka
              </div>
              <h3 className="font-marquee text-[hsl(var(--ink))] text-base leading-tight font-bold line-clamp-2">
                {title}
              </h3>
              <div className="text-[hsl(var(--ink))]/60 text-[11px] mt-1">
                {duration} · €{price}
              </div>
            </div>

            {/* Pseudo player */}
            <div className="flex items-center gap-2.5">
              <div className="h-9 w-9 rounded-full bg-[hsl(var(--red))] grid place-items-center shrink-0">
                <Play size={12} className="text-[hsl(var(--cream))] ml-0.5" fill="currentColor" />
              </div>
              <div className="flex-1">
                <div className="h-1 rounded-full bg-[hsl(var(--ink))]/12 overflow-hidden">
                  <div className="h-full w-[35%] bg-[hsl(var(--ink))]" />
                </div>
                <div className="flex justify-between text-[9px] text-[hsl(var(--ink))]/50 mt-1 font-mono">
                  <span>17:24</span>
                  <span>{duration}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────
   Mockup: BOOK — 3D-ish hardcover card
   ────────────────────────────────────────────── */
const BookMockup = ({
  title,
  author,
  price,
  coverUrl,
}: {
  title: string;
  author: string;
  price: string;
  coverUrl: string | null;
}) => {
  return (
    <div className="relative h-full aspect-[4/5] mx-auto">
      {/* Bauhaus accents */}
      <div className="absolute -top-5 -left-5 w-20 h-20 rounded-full bg-[hsl(var(--red))]" />
      <div className="absolute -bottom-6 -right-2 w-28 h-6 bg-[hsl(var(--yellow))]" />

      {/* Card — mirrors BookCard from /knygynas */}
      <div className="relative h-full rounded-[1.5rem] bg-[hsl(var(--cream-soft))] p-6 flex flex-col items-center justify-between shadow-[0_30px_60px_-25px_rgba(30,30,30,0.4)] border border-[hsl(var(--ink))]/8">
        {/* PRINT badge */}
        <div className="self-start inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[hsl(var(--ink))] text-[hsl(var(--cream))] text-[10px] font-bold tracking-[0.18em] uppercase">
          <BookOpen size={10} /> Knyga
        </div>

        {/* Book cover — exact style from Knygynas (object-contain, no perspective) */}
        <div className="relative flex-1 min-h-0 w-full flex items-center justify-center py-4 overflow-hidden">
          {coverUrl ? (
            <img
              src={coverUrl}
              alt={title}
              className="block w-auto h-full max-h-full max-w-full object-contain"
              loading="eager"
            />
          ) : (
            <div className="h-full aspect-[2/3] grid place-items-center bg-[hsl(var(--terracotta))] text-[hsl(var(--cream))] text-xs font-marquee p-3 text-center rounded-sm">
              {title}
            </div>
          )}
        </div>

        {/* Meta */}
        <div className="text-center w-full">
          <h3 className="font-marquee text-[hsl(var(--ink))] text-base leading-tight font-bold line-clamp-1">
            {title}
          </h3>
          <div className="text-[hsl(var(--ink))]/60 text-[11px] mt-0.5">
            {author} · €{price}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────
   Mockup: WELCOME (logged-in) — Smiley OR Dusia logo (rotates per session)
   ────────────────────────────────────────────── */
const SmileyMockup = () => (
  <div className="relative h-full aspect-square mx-auto">
    <div className="absolute -top-4 -right-4 w-20 h-20 bg-[hsl(var(--red))] rotate-6" />
    <svg viewBox="0 0 400 400" className="relative w-full h-full" aria-hidden>
      <defs>
        <radialGradient id="hero-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="hsl(var(--yellow-glow))" stopOpacity="0.95" />
          <stop offset="60%" stopColor="hsl(var(--yellow-glow))" stopOpacity="0.4" />
          <stop offset="100%" stopColor="hsl(var(--yellow-glow))" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="200" cy="200" r="190" fill="url(#hero-glow)" />
      <circle cx="200" cy="200" r="120" fill="hsl(var(--yellow))" />
      <circle cx="170" cy="180" r="10" fill="hsl(var(--ink))" />
      <circle cx="230" cy="180" r="10" fill="hsl(var(--ink))" />
      <path d="M 165 230 Q 200 255 235 230" stroke="hsl(var(--ink))" strokeWidth="6" fill="none" strokeLinecap="round" />
      <circle cx="200" cy="60" r="6" fill="hsl(var(--ink))" />
    </svg>
  </div>
);

const DusiaLogoMockup = () => (
  <div className="relative h-full aspect-square mx-auto flex items-center justify-center">
    <div className="absolute -top-4 -right-4 w-20 h-20 bg-[hsl(var(--red))] rotate-6" />
    <img
      src={dusiaLogo}
      alt="Dusia"
      className="relative w-full h-full object-contain"
      loading="eager"
    />
  </div>
);

/** Picks Smiley or Dusia logo on each mount (new session/login/refresh). */
const DusiaMockup = () => {
  const [variant] = useState<"smiley" | "dusia">(() =>
    Math.random() < 0.5 ? "smiley" : "dusia"
  );
  return variant === "dusia" ? <DusiaLogoMockup /> : <SmileyMockup />;
};

/* ──────────────────────────────────────────────
   Mockup: PROMO — discount code card (Mother's Day)
   ────────────────────────────────────────────── */
const PromoMockup = ({ code, percent }: { code: string; percent: number }) => (
  <div className="relative h-full aspect-square mx-auto">
    {/* Bauhaus accents */}
    <div className="absolute -top-6 -left-6 w-20 h-20 rounded-full bg-[hsl(var(--yellow))]" />
    <div className="absolute -bottom-4 -right-4 w-16 h-16 bg-[hsl(var(--red))] rotate-12" />

    {/* Ticket card */}
    <div className="relative h-full w-full rounded-[2rem] bg-[hsl(var(--ink))] p-6 shadow-[0_30px_60px_-25px_rgba(30,30,30,0.55)] flex flex-col justify-between overflow-hidden">
      {/* Decorative dotted line */}
      <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 border-t-2 border-dashed border-[hsl(var(--cream))/0.25]" />
      {/* Notches */}
      <div className="absolute -left-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-cream" />
      <div className="absolute -right-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-cream" />

      <div className="relative">
        <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[hsl(var(--yellow))]">
          Mamos dienai
        </div>
        <div className="mt-3 font-serif text-cream leading-none text-[clamp(3.5rem,8vw,5.5rem)] font-bold">
          −{percent}%
        </div>
        <div className="mt-2 text-cream/70 text-sm">
          Visam asortimentui
        </div>
      </div>

      <div className="relative">
        <div className="text-[10px] tracking-[0.25em] uppercase text-cream/50 mb-2">
          Nuolaidos kodas
        </div>
        <div className="rounded-lg border-2 border-dashed border-[hsl(var(--yellow))] bg-[hsl(var(--yellow))/0.08] px-4 py-3 text-center">
          <span className="font-mono font-bold text-[hsl(var(--yellow))] text-xl tracking-[0.2em]">
            {code}
          </span>
        </div>
      </div>
    </div>
  </div>
);

/* ──────────────────────────────────────────────
   Mockup: SALE — pamoku -41% akcija (be kodo, tiesioginė kaina)
   ────────────────────────────────────────────── */
const SaleMockup = ({
  percent,
  salePrice,
  regularPrice,
}: {
  percent: number;
  salePrice: string;
  regularPrice: string;
}) => (
  <div className="relative h-full aspect-square mx-auto">
    <div className="absolute -top-6 -left-6 w-24 h-24 rounded-full bg-[hsl(var(--yellow))]" />
    <div className="absolute -bottom-5 -right-5 w-20 h-20 bg-[hsl(var(--red))] rotate-12" />
    <div className="absolute top-1/2 -right-3 w-4 h-4 rounded-full bg-[hsl(var(--yellow))]" />

    <div className="relative h-full w-full rounded-[2rem] bg-[hsl(var(--ink))] p-8 shadow-[0_30px_60px_-25px_rgba(30,30,30,0.55)] flex flex-col justify-between overflow-hidden">
      <div>
        <div className="inline-flex items-center gap-2">
          <span className="h-1 w-6 bg-[hsl(var(--red))]" />
          <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-[hsl(var(--yellow))]">
            Akcija · Pamokoms
          </span>
        </div>
        <div className="mt-6 font-marquee text-cream leading-none text-[clamp(4.5rem,11vw,7.5rem)] font-bold tracking-tight">
          −{percent}%
        </div>
        <div className="mt-3 text-cream/70 text-sm max-w-[14rem] whitespace-pre-line">
          Visoms audio pamokoms.{"\u00a0\n"}Ribotą laiką.
        </div>
      </div>

      <div className="relative flex items-end gap-3">
        <div>
          <div className="text-[10px] tracking-[0.22em] uppercase text-cream/45 mb-1">
            Dabar
          </div>
          <div className="font-marquee text-[hsl(var(--yellow))] text-4xl font-bold leading-none">
            €{salePrice}
          </div>
        </div>
        <div className="pb-1">
          <div className="text-cream/40 text-base line-through font-mono">
            €{regularPrice}
          </div>
        </div>
      </div>
    </div>
  </div>
);


/* ──────────────────────────────────────────────
   Mockup: ŽEMĖLAPIS — stylized journey map card
   ────────────────────────────────────────────── */
const ZemelapisMockup = () => (
  <div className="relative h-full aspect-square mx-auto flex items-center justify-center">
    <svg viewBox="0 0 400 320" className="relative w-full h-full" aria-hidden>
      {/* Arc */}
      <path
        d="M 90 230 Q 200 70 320 240"
        stroke="hsl(var(--ink))"
        strokeWidth="6"
        fill="none"
        strokeLinecap="round"
      />
      {/* Dots: terracotta (where you are), ink (center), mustard (destination) */}
      <circle cx="140" cy="225" r="16" fill="hsl(var(--terracotta))" />
      <circle cx="205" cy="170" r="9" fill="hsl(var(--ink))" />
      <circle cx="270" cy="150" r="16" fill="hsl(var(--mustard))" />
    </svg>
  </div>
);

/* ──────────────────────────────────────────────
   Mockup: POROS ŽEMĖLAPIS — two arcs meeting
   ────────────────────────────────────────────── */
const PorosMockup = () => (
  <div className="relative h-full aspect-square mx-auto flex items-center justify-center">
    <div className="absolute -top-6 -left-6 w-20 h-20 rounded-full bg-[hsl(var(--yellow))]" />
    <svg viewBox="0 0 400 320" className="relative w-full h-full" aria-hidden>
      <path d="M 60 250 Q 150 60 200 170" stroke="hsl(var(--ink))" strokeWidth="6" fill="none" strokeLinecap="round" />
      <path d="M 340 250 Q 250 60 200 170" stroke="hsl(var(--terracotta))" strokeWidth="6" fill="none" strokeLinecap="round" />
      <circle cx="60" cy="250" r="16" fill="hsl(var(--terracotta))" />
      <circle cx="340" cy="250" r="16" fill="hsl(var(--mustard))" />
      <circle cx="200" cy="170" r="22" fill="hsl(var(--ink))" />
    </svg>
  </div>
);

/* ──────────────────────────────────────────────
   Mockup: ARCHETIPAS — eight-point star / compass
   ────────────────────────────────────────────── */
const ArchetipasMockup = () => (
  <div className="relative h-full aspect-square mx-auto flex items-center justify-center">
    <div className="absolute -bottom-5 -right-5 w-20 h-20 bg-[hsl(var(--red))] rotate-12" />
    <svg viewBox="0 0 400 400" className="relative w-full h-full" aria-hidden>
      <circle cx="200" cy="200" r="150" fill="hsl(var(--yellow))" />
      <circle cx="200" cy="200" r="150" fill="none" stroke="hsl(var(--ink))" strokeWidth="4" />
      <path d="M200 60 L232 168 L340 200 L232 232 L200 340 L168 232 L60 200 L168 168 Z" fill="hsl(var(--ink))" />
      <circle cx="200" cy="200" r="26" fill="hsl(var(--cream))" />
      <circle cx="200" cy="200" r="10" fill="hsl(var(--terracotta))" />
    </svg>
  </div>
);


/* ──────────────────────────────────────────────
   Slide content
   ────────────────────────────────────────────── */
const renderCta = (link: AdLink, variant: "primary" | "secondary") => {
  const isPrimary = variant === "primary";
  const className = isPrimary
    ? "h-12 px-7 rounded-full text-sm font-semibold bg-ink text-cream hover:bg-ink/85 transition-transform hover:scale-[1.02]"
    : "h-12 px-7 rounded-full text-sm font-semibold border-ink/25 text-ink bg-transparent hover:bg-ink/5 transition-transform hover:scale-[1.02]";
  const inner = (
    <>
      {link.text}
      {isPrimary && <ArrowRight size={16} />}
    </>
  );
  return (
    <Button asChild variant={isPrimary ? "default" : "outline"} className={className}>
      {link.external ? (
        <a href={link.href} target="_blank" rel="noopener noreferrer">{inner}</a>
      ) : (
        <Link to={link.href}>{inner}</Link>
      )}
    </Button>
  );
};

const SlideContent = ({ slide }: { slide: AdSlide }) => (
  <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8 items-center">
    <div className="md:col-span-7 max-w-2xl">
      <div className="hidden md:inline-flex items-center gap-2 mb-5">
        <span className="h-1 w-6 bg-[hsl(var(--red))]" />
        <span className="text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--ink))]/60 font-bold">
          {slide.eyebrow}
        </span>
      </div>
      <h1 className="font-marquee text-ink text-[clamp(2.25rem,6.5vw,4.75rem)] leading-[1.02] tracking-tight font-bold mb-5">
        {slide.headline}
      </h1>
      <p className="text-ink/75 text-lg leading-relaxed mb-8 max-w-xl">
        {slide.sub}
      </p>
      {slide.price && (
        <p className="text-ink text-2xl font-bold mb-6" aria-label={`Kaina ${slide.price}`}>
          {slide.price}
        </p>
      )}
      <div className="flex flex-col sm:flex-row gap-3">
        {renderCta(slide.primary, "primary")}
        {slide.secondary && renderCta(slide.secondary, "secondary")}
      </div>
      <div className="md:hidden h-[230px] mt-8 flex items-center justify-center overflow-hidden">
        {slide.mockup}
      </div>
    </div>

    {/* Desktop: visual on right — fixed height so all slides align */}
    <div className="hidden md:flex md:col-span-5 h-[480px] items-center justify-end">
      {slide.mockup}
    </div>
  </div>
);

/* ──────────────────────────────────────────────
   Hero
   ────────────────────────────────────────────── */
const HeroCampaign = () => {
  const { session } = useAuth();
  const userId = session?.user?.id;
  const userMeta = session?.user?.user_metadata as { full_name?: string } | undefined;
  // Profile is the source of truth (users can rename); metadata is only a fallback
  const { data: profileName } = useQuery({
    queryKey: ["hero-profile-name", userId],
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("user_id", userId!)
        .maybeSingle();
      return data?.full_name ?? null;
    },
  });
  const rawName = profileName || userMeta?.full_name || "";
  const firstName = rawName.split(" ")[0] ?? null;
  const { data: purchases } = useLessonPurchases(userId);

  const { data: lesson } = useFeaturedLesson();
  const { data: activeSale } = useActiveLesson();
  const promoUntil = useLessonsPromoUntil();
  const { data: books } = useBooks();
  // Random book pick — new on every mount (login / page refresh)
  const bookSeed = useMemo(() => Math.random(), []);
  const featuredBook = useMemo(() => {
    if (!books || books.length === 0) return undefined;
    return books[Math.floor(bookSeed * books.length)];
  }, [books, bookSeed]);
  const { data: bookImages } = useBookImages(featuredBook?.id);
  const bookCover = bookImages?.[0] ?? featuredBook?.cover_url ?? null;

  const slides = useMemo<AdSlide[]>(() => {
    const list: AdSlide[] = [];

    // 1. Welcome slide for logged-in users — always first
    if (session) {
      const libCount = purchases?.length ?? 0;
      list.push({
        id: "welcome",
        kind: "welcome",
        eyebrow: "Sveika sugrįžus",
        headline: `Gera tave matyti, ${firstName?.trim() ? toVocative(firstName.trim()) : "miela"}.`,
        sub:
          libCount > 0
            ? `Tavo bibliotekoje — ${libCount} ${libCount === 1 ? "pamoka" : libCount < 10 ? "pamokos" : "pamokų"}. Tęskim?`
            : "Šiandien yra puiki diena pradėti.",
        primary: { text: "Mano pamokos", href: "/profilis" },
        secondary: { text: "Naujausia pamoka", href: "/pamokos" },
        mockup: <DusiaMockup />,
      });
    }

    // 2. ŽEMĖLAPIS — naujiena
    list.push({
      id: "naujiena-zemelapis",
      kind: "announcement",
      eyebrow: "Naujiena · Žemėlapis",
      headline: "Atrask, kur esi ir kur nori būti.",
      sub: "Leisk sau pirmą kartą pamatyti savo gyvenimą iš šalies ir perskaityti istoriją apie save.",
      primary: { text: "Į Žemėlapį", href: "/zemelapis" },
      mockup: <ZemelapisMockup />,
    });

    // 2b. POROS ŽEMĖLAPIS — naujiena
    list.push({
      id: "naujiena-poros-zemelapis",
      kind: "announcement",
      eyebrow: "Naujiena · Poroms",
      headline: "Porų žemėlapis — vienas kelias dviese.",
      sub: "Atsakykite atskirai, pamatykite kartu. Ataskaita apie jūsų santykį — kur sutampate ir kur prasilenkiate.",
      primary: { text: "Į porų žemėlapį", href: "/poros-zemelapis" },
      mockup: <PorosMockup />,
    });

    // 2c. ARCHETIPAS — nemokamas testas
    list.push({
      id: "naujiena-archetipas",
      kind: "announcement",
      eyebrow: "Naujiena · Nemokamas testas",
      headline: "Koks tavo archetipas?",
      sub: "Dvidešimt klausimų ir tekstas apie tave, kurį norėsis pasilikti. Nemokamai.",
      primary: { text: "Pradėti testą", href: "/archetipas" },
      mockup: <ArchetipasMockup />,
    });

    // 3. SALE — aktyvi akcija
    const salePricing = activeSale ? resolveLessonPrice(activeSale, promoUntil) : null;
    if (activeSale && salePricing?.onSale) {
      const salePrice = salePricing.amount.toFixed(2).replace(".", ",");
      const regularPrice = salePricing.regularPrice.toFixed(2).replace(".", ",");
      const percent = salePricing.discountPercent;
      list.push({
        id: "sale-pamokos",
        kind: "promo",
        eyebrow: "Akcija · Audio pamokoms",
        headline: `−${percent}%`,
        sub: `Visoms audio pamokoms. Ribotą laiką.\u00a0\nDabar — €${salePrice} (buvo €${regularPrice}).`,
        price: `€${salePrice}`,
        primary: { text: "Į pamokas", href: "/pamokos" },
        mockup: (

          <SaleMockup
            percent={percent}
            salePrice={salePrice}
            regularPrice={regularPrice}
          />
        ),
      });
    }

    // 4. LECTURE ad
    if (lesson) {
      const price = resolveLessonPrice(lesson, promoUntil).amount.toFixed(2).replace(".", ",");
      list.push({
        id: `lecture-${lesson.id}`,
        kind: "lecture",
        eyebrow: "Audio pamoka",
        headline: lesson.title,
        sub: lesson.description
          ? smartTruncate(lesson.description, 160)
          : "Klausyk bet kur. Pirma minutė — nemokamai.",
        price: `€${price}`,
        primary: { text: "Klausyti dabar", href: `/pamokos?lesson=${lesson.id}` },
        secondary: { text: "Visos pamokos", href: "/pamokos" },
        mockup: (
          <LectureMockup
            title={lesson.title}
            duration={lesson.duration ?? "~50 min"}
            price={price}
            artSeed={lesson.id}
          />
        ),
      });
    } else {
      // Generic lecture ad
      list.push({
        id: "lecture-generic",
        kind: "lecture",
        eyebrow: "Audio pamokos",
        headline: "Pamokos, kurias nešiesi su savimi.",
        sub: "50 minučių apie save — kelyje, virtuvėje, vakare. Pirma minutė nemokamai.",
        price: "€49,90",
        primary: { text: "Pamokos", href: "/pamokos" },
        mockup: (
          <LectureMockup
            title="Tapk charizmatiška"
            duration="~50 min"
            price="49,90"
            artSeed="hero-lecture"
          />
        ),
      });
    }

    // 4. BOOK ad
    if (featuredBook) {
      const price = Number(featuredBook.price ?? 0).toFixed(2).replace(".", ",");
      list.push({
        id: `book-${featuredBook.id}`,
        kind: "book",
        eyebrow: "Knyga",
        headline: featuredBook.title,
        sub: featuredBook.description
          ? smartTruncate(featuredBook.description, 160)
          : "Spausdinta knyga, kuri lieka rankose ir širdyje.",
        primary: { text: "Į knygyną", href: (featuredBook as any).slug ? `/knygynas/${(featuredBook as any).slug}` : `/knygynas?book=${featuredBook.id}` },
        secondary: { text: "Visos knygos", href: "/knygynas" },
        mockup: (
          <BookMockup
            title={featuredBook.title}
            author={featuredBook.author ?? "Laimės Dieta"}
            price={price}
            coverUrl={getThumbUrl(bookCover)}
          />
        ),
      });
    } else {
      list.push({
        id: "book-generic",
        kind: "book",
        eyebrow: "Knygynas",
        headline: "Knygos, kurios lieka rankose.",
        sub: "Spausdinti leidiniai, sukurti su meile. Pristatymas Lietuvoje.",
        primary: { text: "Į knygyną", href: "/knygynas" },
        mockup: (
          <BookMockup
            title="Laimės dieta"
            author="Laimės Dieta"
            price="19,90"
            coverUrl={null}
          />
        ),
      });
    }

    return list;
  }, [session, firstName, purchases, lesson, activeSale, promoUntil, featuredBook, bookCover]);

  // A single offer stays in place while the visitor reads. Other products remain below.
  const slide = slides.find((candidate) => candidate.kind === "promo")
    ?? slides.find((candidate) => candidate.kind === "lecture")
    ?? slides[0];
  if (!slide) return null;

  return (
    <section className="relative bg-cream pt-20 pb-10 md:py-24 min-h-[560px] flex items-center overflow-hidden">
      <div className="container mx-auto px-6">
        <div>
          <SlideContent slide={slide} />
        </div>
      </div>
    </section>
  );
};

export default HeroCampaign;
